import { Button, Card, Container, Typography } from "@material-ui/core";
import DropOffManageRacksAndBins from "@saleor/dropOff/components/DropOffDispatch/DropOffManageRacksAndBins";
import DropOffHomeLocationAndTaskArea from "@saleor/dropOff/components/DropOffHomeLocation/DropOffHomeLocationAndTaskArea";
import DropOffManageTaskAreas from "@saleor/dropOff/components/DropOffTaskArea/DropOffManageTaskAreas";
import {
  useDropOffMallQuery,
  useGetTemplateQuery
} from "@saleor/dropOff/queries";
import { useTemplateStyles } from "@saleor/dropOff/styles/templates";
import {
  dropOffListPath,
  DropOffListUrlQueryParams,
  dropOffUrl
} from "@saleor/dropOff/url";
import { TabValuesEnum } from "@saleor/dropOff/utils/filters";
import {
  TemplateAction,
  userCanManageTaskAreas
} from "@saleor/dropOff/utils/handlers";
import useNavigator from "@saleor/hooks/useNavigator";
import useUser from "@saleor/hooks/useUser";
import { stringifyQs } from "@saleor/utils/urls";
import React from "react";
import { FormattedMessage } from "react-intl";
import urlJoin from "url-join";

interface DropOffViewTemplateProps {
  params: DropOffListUrlQueryParams;
  id: string;
}

const DropOffViewTemplate: React.FC<DropOffViewTemplateProps> = props => {
  const { params, id } = props;

  const classes = useTemplateStyles(props);
  const navigate = useNavigator();
  const user = useUser();

  const [currentStep, setCurrentStep] = React.useState(params?.step);

  React.useEffect(() => {
    setCurrentStep(params?.step);
  }, [params?.step]);

  const { data: template } = useGetTemplateQuery({
    displayLoader: true,
    variables: {
      first: 100,
      id
    }
  });

  const { data: mall } = useDropOffMallQuery({
    displayLoader: true,
    skip: !params?.mall || params?.mall.length === 0,
    variables: {
      id: params?.mall
    }
  });

  const handleBackForVIewFromStep2 = (templateId, mallId) => {
    navigate(
      urlJoin(
        dropOffUrl(templateId),
        "?" +
          stringifyQs({
            mall: mallId,
            step: 1
          })
      )
    );
  };

  const handleBackForVIewFromStep3 = (templateId, mallId) => {
    navigate(
      urlJoin(
        dropOffUrl(templateId),
        "?" +
          stringifyQs({
            mall: mallId,
            step: 2
          })
      )
    );
  };

  const handleBackForViewTemplate = () => {
    navigate(
      urlJoin(
        dropOffListPath,
        "?" +
          stringifyQs({
            mall: params?.mall,
            tab: TabValuesEnum.TEMPLATES
          })
      )
    );
  };

  const handleNextForVIewFromStep2 = (templateId, mallId) => {
    navigate(
      urlJoin(
        dropOffUrl(templateId),
        "?" +
          stringifyQs({
            mall: mallId,
            step: 3
          })
      )
    );
  };

  return (
    <Container>
      <div>
        <div className={classes.headerSection}>
          <div className={classes.headerLeft}>
            <div className={classes.headerTitleMall}>
              <h5 className={classes.headerTitle}>
                {template?.template?.name}
              </h5>
              <Typography color="primary" className={classes.mallTitle}>
                {mall?.mall?.name}
              </Typography>
            </div>
          </div>

          <div className={classes.headerLeft}>
            <Button
              size="large"
              className={classes.btnTopCreateNewTemplate}
              color="primary"
              variant="contained"
              onClick={() => handleBackForViewTemplate()}
            >
              <FormattedMessage defaultMessage="Back" description="button" />
            </Button>
          </div>
        </div>

        <Card className={classes.dropOffCard} style={{ padding: "40px 0px" }}>
          {(!currentStep || currentStep.toString() === "1") && (
            <DropOffHomeLocationAndTaskArea
              mallId={mall?.mall?.id}
              templateId={template?.template?.id}
              templateAction={TemplateAction.VIEW}
              editHomeLocation={
                template?.template?.dropOffLocations?.edges?.find(
                  node => node.node.defaultDropOffLocation === true
                )?.node
              }
            />
          )}

          {!!currentStep && currentStep.toString() === "2" && (
            <DropOffManageRacksAndBins
              key={template?.template?.id}
              templateId={template?.template?.id}
              templateAction={TemplateAction.VIEW}
              onBack={() => {
                handleBackForVIewFromStep2(
                  template?.template?.id,
                  mall?.mall?.id
                );
              }}
              nextStep={() => {
                handleNextForVIewFromStep2(
                  template?.template?.id,
                  mall?.mall?.id
                );
              }}
            />
          )}

          {!!currentStep && currentStep.toString() === "3" && (
            <DropOffManageTaskAreas
              disableRename={!userCanManageTaskAreas(user?.user)}
              disableAddRemove={!userCanManageTaskAreas(user?.user)}
              mallId={mall?.mall?.id}
              templateId={template?.template?.id}
              templateAction={TemplateAction.VIEW}
              clickBackForEditTemplate={handleBackForVIewFromStep3}
            />
          )}
        </Card>
      </div>
    </Container>
  );
};

export default DropOffViewTemplate;
